/**
 * The user's threads.
 *
 * Server cache, unlike the turns themselves: this is a list that is fetched and
 * invalidated, not a stream that is appended to. It changes on exactly three events — a
 * turn finishing, a new thread starting, and a rename or delete — and each of those
 * invalidates it rather than patching it, so the order the server decides is the order
 * shown.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { chatApi } from '@/lib/api/chat'
import { queryKeys } from '@/lib/queryClient'

export function useConversations() {
  return useQuery({
    queryKey: queryKeys.conversations,
    queryFn: chatApi.conversations,
    staleTime: 10_000,
  })
}

export function useRenameConversation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      chatApi.renameConversation(id, title),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.conversations }),
  })
}

export function useDeleteConversation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => chatApi.deleteConversation(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.conversations }),
  })
}
